import React, { FC, HTMLAttributes } from 'react'
import { Box, SimpleGrid } from '@chakra-ui/core'
import { Hook } from '@engyalo/storefront-types'
import { IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { ProductItem } from '../ProductItem'
import { usePlugin } from '../../Plugin'
import { AutoSizer, List, ListRowProps, Size } from 'react-virtualized'

export enum ProductListFrom {
  COLLECTIONS = 'collections',
  SEARCH = 'search',
}

interface ICustomProduct extends IProduct {
  enabledPrice?: boolean
}

interface IProps extends HTMLAttributes<HTMLDivElement> {
  products: ICustomProduct[]
  from: ProductListFrom
  isLoading?: boolean
  showPrice?: boolean
  useVirtualizedList?: boolean
}

const HOOK_FROM_LOCATION: { [key in ProductListFrom]: Hook } = {
  [ProductListFrom.COLLECTIONS]: Hook.CATALOGUE,
  [ProductListFrom.SEARCH]: Hook.SEARCH_CATALOGUE,
}

export const ProductList: FC<IProps> = ({ products, from, isLoading = false, showPrice = true, useVirtualizedList = false }: IProps) => {
  const plugin = usePlugin(HOOK_FROM_LOCATION[from])
  const CustomContainer = plugin?.container ?? Box
  const pluginProps = plugin?.props ?? {}

  const renderRow = ({ index, key, style, isScrolling, isVisible }: ListRowProps): JSX.Element => {
    const index1 = index * 2
    const index2 = index1 + 1
    const product = products[index]

    if (products[index1] && products[index2]) {
      return (
        <SimpleGrid
          columns={2}
          spacing={5}
          style={{
            ...style,
            paddingLeft: 5,
            paddingRight: 5,
            paddingBottom: 10,
            marginTop: 10,
          }}
          key={key}
        >
          <ProductItem
            style={{ color: 'black' }}
            key={products[index1].name ?? `product-${index1}`}
            product={products[index1]}
            isLoading={isLoading}
            showPrice={products[index1]?.enabledPrice || showPrice}
          />
          <ProductItem
            style={{ color: 'black' }}
            key={products[index2].name ?? `product-${index2}`}
            product={products[index2]}
            isLoading={isLoading}
            showPrice={products[index2]?.enabledPrice || showPrice}
          />
        </SimpleGrid>
      )
    } else if (products[index1]) {
      return (
        <SimpleGrid
          columns={2}
          spacing={5}
          style={{
            ...style,
            paddingLeft: 5,
            paddingRight: 5,
            paddingBottom: 10,
            marginTop: 10,
          }}
          key={key}
        >
          <ProductItem
            style={{ color: 'black' }}
            key={products[index1].name ?? `product-${index1}`}
            product={products[index1]}
            isLoading={isLoading}
            showPrice={products[index1]?.enabledPrice || showPrice}
          />
        </SimpleGrid>
      )
    } else {
      return <></>
    }
  }

  const renderList = ({width, height}: Size): JSX.Element => { 
    const rowHeight = showPrice ? 250 : 216
    return (
      <List
          width={width - 15}
          height={height - 100}
          rowHeight={rowHeight}
          rowRenderer={renderRow}
          rowCount={Math.ceil(products.length / 2)}
          style={{
            paddingBottom: 50,
          }}
        />
    )
  }

  return (
    <CustomContainer
      display="grid"
      gridTemplateColumns="47% 47%"
      gridGap="8px 8px"
      justifyContent="center"
      {...pluginProps}
      height={useVirtualizedList ? "100vh" : "auto"}
    >
      {useVirtualizedList ?
      <AutoSizer>
        {renderList}
      </AutoSizer>
      :
        products.map((product, index) => (
          <ProductItem
            key={product.name ?? `product-${index}`}
            product={product}
            isLoading={isLoading}
            showPrice={product?.enabledPrice || showPrice}
          />
        ))
      }
    </CustomContainer>
  )
}
